import { createClient, PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { useAuthStore } from '../stores/authStore';

// Pobieramy dane z zmiennych środowiskowych
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase configuration. Please check your environment variables.');
}

// Definiujemy standardowe nagłówki dla wszystkich zapytań - TYLKO apikey i Content-Type/Accept
const baseSupabaseHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'apikey': supabaseKey, // Zawsze wymagany przez Supabase
  'X-Client-Info': 'career-advisor-app'
};

// Inicjalizacja klienta Supabase - globalne nagłówki mogą być prostsze
export const supabaseClient: SupabaseClient = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true
    // detectSessionInUrl: false, // Można rozważyć, jeśli są problemy z sesją w URL
  },
  // global: {} // Można usunąć global, klient zarządza tym sam
});

// Mechanizm buforowania odpowiedzi z Supabase
const supabaseCache = new Map<string, {
  data: any,
  timestamp: number
}>();

// Czas ważności cache w milisekundach (default: 5 minut)
const CACHE_TTL = 1000 * 60 * 5;

// Ustawienie elementu w cache
const setCacheItem = (key: string, data: any) => {
  supabaseCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Pobranie elementu z cache
const getCacheItem = (key: string) => {
  const item = supabaseCache.get(key);
  if (!item) return null;
  
  // Sprawdzenie czy element nie wygasł
  if (Date.now() - item.timestamp > CACHE_TTL) {
    supabaseCache.delete(key);
    return null;
  }
  
  return item.data;
};

// Usunięcie elementu z cache
const removeCacheItem = (key: string) => {
  supabaseCache.delete(key);
};

// Wyczyszczenie całego cache
export const clearCache = () => {
  supabaseCache.clear();
};

// Funkcja do pobierania danych z Supabase - UŻYWA TERAZ supabaseClient
export const fetchFromSupabase = async <T>({
  tableName,
  columns = '*',
  filters = {},
  limit = 100,
  orderBy = null,
  orderDirection = 'desc',
  useCache = true,
  cacheKey = '',
  single = false // Dodajemy opcję pobrania pojedynczego rekordu
}: {
  tableName: string;
  columns?: string;
  filters?: Record<string, any>;
  limit?: number;
  orderBy?: string | null;
  orderDirection?: 'asc' | 'desc';
  useCache?: boolean;
  cacheKey?: string;
  single?: boolean; // Czy oczekujemy jednego wyniku?
}) => {
  const finalCacheKey = cacheKey || `${tableName}:${JSON.stringify(filters)}:${limit}:${orderBy}:${orderDirection}:${single}`;

  try {
    if (useCache) {
      const cachedData = getCacheItem(finalCacheKey);
      if (cachedData) {
        console.log(`[SupabaseFetch] Używam danych z cache dla: ${tableName}`);
        // Zwracamy dane w odpowiednim formacie (obiekt lub tablica)
        return { data: cachedData as (single extends true ? T | null : T[]), error: null };
      }
    }

    console.log(`[SupabaseFetch] Pobieranie danych z ${tableName} (client) z filtrami:`, filters);

    let query = supabaseClient.from(tableName).select(columns);

    // Dodawanie filtrów
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Proste filtrowanie 'eq', można rozbudować o inne operatory (in, gte, etc.)
        query = query.eq(key, value);
      }
    });

    // Sortowanie
    if (orderBy) {
      query = query.order(orderBy, { ascending: orderDirection === 'asc' });
    }

    // Limit
    if (limit && !single) { // Nie limitujemy jeśli chcemy .single()
      query = query.limit(limit);
    }

    // Wykonanie zapytania
    let result;
    if (single) {
      result = await query.maybeSingle(); // Używamy maybeSingle() - zwraca null zamiast błędu PGRST116
    } else {
      result = await query;
    }

    const { data, error } = result;

    if (error) {
      // Logowanie błędów specyficznych dla Supabase
      console.error(`[SupabaseFetch] Błąd Supabase podczas pobierania z ${tableName}:`, error);
      // Zwracamy błąd w standardowym formacie
      return { data: null, error: { message: error.message, code: error.code, details: error.details, hint: error.hint } };
    }

    console.log(`[SupabaseFetch] Pobrano dane z ${tableName}`);

    // Zapis do cache
    if (useCache && data !== null) { // Sprawdzamy czy data nie jest null (ważne dla maybeSingle)
      setCacheItem(finalCacheKey, data);
    }

    // Zwracamy dane w odpowiednim typie
    return { data: data as (single extends true ? T | null : T[]), error: null };

  } catch (error) {
    // Ogólny błąd
    console.error(`[SupabaseFetch] Nieoczekiwany błąd podczas pobierania danych z ${tableName}:`, error);
    // Próba fallbacku do cache
    if (useCache) {
      const cachedData = getCacheItem(finalCacheKey);
      if (cachedData) {
        console.log(`[SupabaseFetch] Używam danych z cache jako fallback dla błędu: ${tableName}`);
        return { data: cachedData as (single extends true ? T | null : T[]), error: null };
      }
    }
    return { data: null, error: error };
  }
};

/**
 * Zapisuje dane do Supabase - UŻYWA TERAZ supabaseClient
 */
export async function saveToSupabase<T = any>({
  tableName,
  data,
  options = {}
}: {
  tableName: string;
  data: Record<string, any> | Record<string, any>[]; // Może przyjmować obiekt lub tablicę obiektów
  options?: {
    updateExisting?: boolean; // Czy użyć UPSERT
    // primaryKey?: string; // Niepotrzebne przy upsert klienta
    // mergeDefault?: boolean; // Klient domyślnie zarządza wartościami domyślnymi
    returning?: 'minimal' | 'representation'; // Co ma zwrócić Supabase
  }
}): Promise<{ data: T | T[] | null; error: any }> {
  const { updateExisting = false, returning = 'representation' } = options;

  try {
    console.log(`[SupabaseSave] Zapisywanie danych do ${tableName} (client)`);

    let query;
    if (updateExisting) {
      // Używamy upsert - automatycznie wstawi lub zaktualizuje na podstawie klucza głównego
      query = supabaseClient.from(tableName).upsert(data, {
        // ignoreDuplicates: false, // Domyślnie false, nie ignoruje duplikatów (ważne dla UPSERT)
        // onConflict: primaryKey // Można podać kolumnę konfliktu, jeśli inna niż PK
      }).select(); // Dodajemy select(), aby zwróciło zapisane dane
    } else {
      // Zwykły insert
      query = supabaseClient.from(tableName).insert(data).select(); // Dodajemy select()
    }

    // Ustawienie preferencji zwracania danych
    // if (returning === 'representation') {
    //   query = query.select(); // .select() jest teraz domyślnie dodawane
    // }
    // Klient Supabase v2 domyślnie używa 'return=representation' dla insert/upsert/update/delete z .select()

    const { data: savedData, error } = await query;

    if (error) {
      console.error(`[SupabaseSave] Błąd Supabase podczas zapisywania do ${tableName}:`, error);
      return { data: null, error: { message: error.message, code: error.code, details: error.details, hint: error.hint } };
    }

    console.log(`[SupabaseSave] Pomyślnie zapisano dane w ${tableName}`);

    // Wyczyść cache dla tej tabeli
    clearCacheForTable(tableName);

    // Zwróć zapisane dane (mogą być tablicą lub pojedynczym obiektem w zależności od zapytania)
    return { data: savedData as T | T[] | null, error: null };

  } catch (error) {
    console.error(`[SupabaseSave] Nieoczekiwany błąd podczas zapisywania do ${tableName}:`, error);
    return { data: null, error };
  }
}

// Funkcja pomocnicza do czyszczenia cache dla konkretnej tabeli
const clearCacheForTable = (tableName: string) => {
  console.log(`[SupabaseCache] Czyszczenie cache dla tabeli: ${tableName}`);
  for (const key of supabaseCache.keys()) {
    if (key.startsWith(`${tableName}:`)) {
      removeCacheItem(key);
    }
  }
};


// Uproszczona obsługa zmiany stanu autoryzacji - dodajmy logowanie błędów
supabaseClient.auth.onAuthStateChange((event, session) => {
  console.log('[Auth] Auth state changed:', event, session ? `User: ${session.user.id}` : 'No session');

  try {
    switch (event) {
      case 'SIGNED_IN':
        if (session?.user) {
          useAuthStore.setState({
            user: session.user,
            session,
            initialized: true,
            loading: false,
            error: null
          });
          console.log('[Auth] User signed in, state updated.');
          // Można tutaj wywołać np. fetch danych użytkownika
        } else {
           console.warn('[Auth] SIGNED_IN event without session/user.');
        }
        break;
      case 'SIGNED_OUT':
        useAuthStore.setState({
          user: null,
          session: null,
          initialized: true, // Nadal zainicjowane, ale bez użytkownika
          loading: false,
          error: null
        });
        console.log('[Auth] User signed out, state cleared.');
        clearCache(); // Wyczyść cache przy wylogowaniu
        break;
      case 'TOKEN_REFRESHED':
        if (session) {
          useAuthStore.setState({ session });
           console.log('[Auth] Token refreshed.');
        } else {
           console.warn('[Auth] TOKEN_REFRESHED event without session.');
           // Jeśli odświeżenie tokena zawiodło, może warto wylogować?
           useAuthStore.setState({ user: null, session: null });
           clearCache();
        }
        break;
      case 'USER_UPDATED':
        if (session?.user) {
          useAuthStore.setState({
            user: session.user, // Zaktualizuj dane użytkownika
            session, // Zaktualizuj sesję na wszelki wypadek
            loading: false
          });
           console.log('[Auth] User data updated.');
        }
        break;
      case 'PASSWORD_RECOVERY':
         console.log('[Auth] Password recovery process started.');
        break;
      case 'INITIAL_SESSION':
         console.log('[Auth] Initial session check complete.');
         // Ustaw initialized na true, nawet jeśli nie ma sesji
         if (!useAuthStore.getState().initialized) {
            useAuthStore.setState({ initialized: true, loading: false });
         }
         break;
      default:
         console.log('[Auth] Unhandled auth event:', event);
    }
  } catch (error) {
     console.error('[Auth] Error handling auth state change:', event, error);
     // Ustaw stan błędu w store, jeśli to możliwe
     useAuthStore.setState({ error: 'Błąd obsługi stanu autoryzacji', loading: false });
  }
});


// Funkcja pomocnicza do dodawania nagłówków - nie jest już potrzebna w tej formie
// export const addHeaders = (headers = {}) => { ... };
